import { useState } from 'react';

import DownloadDoneIcon from '@mui/icons-material/DownloadDone';
import DownloadingIcon from '@mui/icons-material/Downloading';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import Resolver from 'src/common/Resolver';
import QrCheckinScannerDialog from 'src/common/components/QrCheckinScannerDialog';
import { dashboardPrimaryActionButtonProps } from 'src/common/components/DashboardPageToolbar';
import DashboardContentPageLayout from 'src/common/components/dashboard/DashboardContentPageLayout';
import EditionCompetitionToolbar from 'src/common/components/dashboard/EditionCompetitionToolbar';
import EditionYearFilter from 'src/common/components/dashboard/EditionYearFilter';
import Icon from 'src/common/components/icons/Icon';
import TicketCard from 'src/common/components/styled/TicketCard';
import { LanguageKeys } from 'src/common/constants/languages';
import { useEditionCompetitionQueryParams } from 'src/hooks/useEditionCompetitionQueryParams';
import { useQrStopTimerFlow } from 'src/hooks/useQrStopTimerFlow';
import { ticketCardGridItemSx } from 'src/common/styles/ticketCardStyles';
import useCheckinsStore from 'src/stores/checkinsStore';
import useParticipantsStore from 'src/stores/participantsStore';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';
import Competition from './Competition';

export default function CheckinPage() {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;
  const { enqueueSnackbar } = useSnackbar();

  const { fetchParticipants: doFetchParticipants } = useParticipantsStore(s => s);
  const { checkins, fetchCheckinsByCompetitionId: doFetchCheckinsByCompetitionId } =
    useCheckinsStore(s => s);
  const { activeEditionId, dateTimeFormat, distancesById } = useSystemOptionsStore(s => s);

  const {
    selectedYear,
    selectedCompetitionName,
    selectedDistanceName,
    selectedDistanceId,
    selectedEdition,
    competition,
    competitionsForEdition,
    distancesForCompetition,
    distances,
    setYear,
    setCompetition,
    setDistance,
  } = useEditionCompetitionQueryParams();

  const isDetailView = Boolean(competition && selectedCompetitionName && selectedEdition);
  const isCurrentEdition = Boolean(activeEditionId && selectedEdition?.id === activeEditionId);
  const hubBackHref =
    selectedYear != null ? `/dashboard/competition?year=${selectedYear}` : '/dashboard/competition';

  const visibleDistances = selectedDistanceId
    ? distances.filter(id => id === selectedDistanceId)
    : distances;

  const pageTitle = isDetailView
    ? t('competitions.pageTitle', { event: competition!.i18n[currentLang] })
    : t('drawer.checkins');

  const [isDownloading, setIsDownloading] = useState<'idle' | 'yes' | 'finished'>('idle');

  const {
    qrStopScannerOpen,
    openQrStopScanner,
    closeQrStopScanner,
    handleQrStopTimerScan,
  } = useQrStopTimerFlow({
    editionId: selectedEdition?.id,
    competitionId: competition?.id,
    checkins,
  });

  const exportToExcel = () => {
    if (!competition || !selectedEdition || !distancesById) return;
    setIsDownloading('yes');
    const competitionName = competition.i18n[currentLang];
    const fileName = `${competitionName} ${selectedEdition.year}`;
    void import('src/helpers/export')
      .then(({ exportCompetitionCheckinsToExcel }) =>
        exportCompetitionCheckinsToExcel({
          checkins,
          distanceIds: visibleDistances,
          distancesById,
          currentLang,
          dateTimeFormat,
          translate: t,
          fileName,
          sheetName: competitionName,
        }),
      )
      .catch(() => {
        enqueueSnackbar(t('checkins.errors.exportFailed'), { variant: 'error' });
      })
      .finally(() => {
        setIsDownloading('finished');
      });
  };

  const toolbar = isDetailView ? (
    <EditionCompetitionToolbar
      selectedYear={selectedYear}
      selectedCompetitionName={selectedCompetitionName}
      competitionsForEdition={competitionsForEdition}
      currentLang={currentLang}
      onYearChange={setYear}
      onCompetitionChange={setCompetition}
      allowAllCompetitions={false}
      showDistanceFilter
      distancesForCompetition={distancesForCompetition}
      selectedDistanceName={selectedDistanceName}
      onDistanceChange={setDistance}
      allowAllDistances
    />
  ) : (
    <EditionYearFilter selectedYear={selectedYear} onYearChange={setYear} />
  );

  const toolbarActions = isDetailView ? (
    <>
      <Button
        {...dashboardPrimaryActionButtonProps()}
        aria-label={t('export.export')}
        onClick={exportToExcel}
        startIcon={
          isDownloading === 'yes' ? (
            <DownloadingIcon />
          ) : isDownloading === 'finished' ? (
            <DownloadDoneIcon />
          ) : (
            <Icon icon="xml" />
          )
        }
      >
        {t('export.export')}
      </Button>
      <Tooltip
        title={
          isCurrentEdition
            ? t('timers.scanQrStopTimer')
            : t('timers.bulkActionsDisabledNotCurrentEdition')
        }
      >
        <span>
          <IconButton
            aria-label={t('timers.scanQrStopTimer')}
            disabled={!isCurrentEdition}
            onClick={openQrStopScanner}
          >
            <QrCodeScannerIcon />
          </IconButton>
        </span>
      </Tooltip>
    </>
  ) : undefined;

  return (
    <>
      <DashboardContentPageLayout
        title={pageTitle}
        toolbar={toolbar}
        toolbarActions={toolbarActions}
        backLink={
          isDetailView ? { label: t('checkins.backToHub'), href: hubBackHref } : undefined
        }
      >
        {isDetailView && competition && selectedEdition ? (
          <Resolver
            key={`${selectedEdition.id}:${competition.id}`}
            resolve={() =>
              Promise.all([
                doFetchParticipants(),
                doFetchCheckinsByCompetitionId(selectedEdition.id, competition.id),
              ])
            }
            successComponent={Competition}
            successProps={{
              competition,
              distances: visibleDistances,
              editionId: selectedEdition.id,
            }}
          />
        ) : !selectedEdition ? (
          <Typography variant="body1" sx={{ my: 2 }}>
            {t('insights.selectEdition')}
          </Typography>
        ) : competitionsForEdition.length === 0 ? (
          <Typography variant="body1" sx={{ my: 2 }}>
            {t('competitions.noCompetitionsInEdition')}
          </Typography>
        ) : (
          <Grid container spacing={2} sx={{ overflow: 'visible' }}>
            {competitionsForEdition.map(comp => (
              <Grid key={comp.id} size={{ xs: 12, sm: 6, md: 4 }} sx={ticketCardGridItemSx}>
                <Box
                  component={RouterLink}
                  to={`/dashboard/competition?year=${selectedEdition.year}&competition=${comp.name}`}
                  sx={{ textDecoration: 'none', color: 'inherit', height: '100%', display: 'block' }}
                >
                  <TicketCard
                    eyebrow={t('drawer.checkins')}
                    title={comp.i18n[currentLang]}
                    avatar={
                      <Icon
                        icon={comp.icon}
                        fontSize="large"
                        sx={{ width: '100%', height: '100%' }}
                      />
                    }
                    sx={{ height: '100%' }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </DashboardContentPageLayout>

      {isDetailView ? (
        <QrCheckinScannerDialog
          open={qrStopScannerOpen}
          onClose={closeQrStopScanner}
          onScan={handleQrStopTimerScan}
        />
      ) : null}
    </>
  );
}
